import React, { useState, useMemo } from "react";
import { Dialog } from "@/components/dialog";
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from "@/components/table";
import { Input, InputGroup } from "@/components/input";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";
import useQueryGetUserList from "@/apis/users/getUserList";
import { Select } from "@/components/select";

interface StudentSelectModalProps {
  open: boolean;
  onClose: () => void;
  selectedUserIds: string[]; // 이미 추가된 학생들
  onSelect: (newUserIds: string[]) => void; // 새로 추가할 학생들만
}

const StudentSelectModal: React.FC<StudentSelectModalProps> = ({
  open,
  onClose,
  selectedUserIds,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [newlySelected, setNewlySelected] = useState<string[]>([]); // 새로 추가할 학생들만
  const [selectedGroupId, setSelectedGroupId] = useState<string | "">("");

  React.useEffect(() => {
    setNewlySelected([]); // 모달이 열릴 때마다 초기화
  }, [open]);

  // 그룹 목록 불러오기
  const { data: groupListData } = useQueryGetGroupList();
  const groupList = groupListData?.items || [];

  // 사용자 목록 불러오기 (그룹 ID에 따라 필터링)
  const userListParams = useMemo(() => {
    const params: any = {
      page: 1,
      limit: 1000, // 충분히 큰 수로 설정하여 모든 사용자 가져오기
      role: "student",
    };

    if (selectedGroupId) {
      params.group_id = selectedGroupId;
    }

    return params;
  }, [selectedGroupId]);

  const { data: usersData } = useQueryGetUserList(userListParams);

  // 표시할 학생 목록 (이미 추가된 학생들은 제외하지 않고 모두 표시)
  const displayedUsers = useMemo(() => {
    const users = usersData?.items || [];
    return users.filter(
      (user: any) =>
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
    );
  }, [usersData, search]);

  // 추가 가능한 학생들 (이미 추가된 학생들 제외)
  const availableUsers = useMemo(() => {
    return displayedUsers.filter((user: any) => !selectedUserIds.includes(user.id));
  }, [displayedUsers, selectedUserIds]);

  // 전체 선택 체크박스 (추가 가능한 학생들만 대상)
  const handleGroupSelectAll = () => {
    const allNewlySelected = availableUsers.every((u: any) =>
      newlySelected.includes(u.id)
    );
    if (allNewlySelected) {
      setNewlySelected(
        newlySelected.filter((id: string) => !availableUsers.some((u: any) => u.id === id))
      );
    } else {
      setNewlySelected([
        ...newlySelected,
        ...availableUsers
          .map((u: any) => u.id)
          .filter((id: string) => !newlySelected.includes(id)),
      ]);
    }
  };

  const toggleUser = (userId: string) => {
    // 이미 추가된 학생은 토글할 수 없음
    if (selectedUserIds.includes(userId)) return;
    
    setNewlySelected((prev: string[]) =>
      prev.includes(userId)
        ? prev.filter((id: string) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleRowClick = (userId: string) => {
    toggleUser(userId);
  };

  const handleSelect = () => {
    onSelect(newlySelected);
  };

  return (
    <Dialog
      title="Add Students"
      open={open}
      onClose={onClose}
      size="3xl"
      className="bg-black"
    >
      <div className="mb-4 flex items-center gap-2">
        <Select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          className="max-w-[240px]"
        >
          <option value="">Select group</option>
          {groupList.map((group: any) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>
        <div className="flex-1" />
        <InputGroup>
          <div className="flex">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
            />
            <Button onClick={() => setSearch(search)} className="ml-2">
              Search
            </Button>
          </div>
        </InputGroup>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>
                <Checkbox
                  checked={
                    availableUsers.length > 0 &&
                    availableUsers.every((u: any) => newlySelected.includes(u.id))
                  }
                  indeterminate={
                    availableUsers.some((u: any) => newlySelected.includes(u.id)) &&
                    !availableUsers.every((u: any) => newlySelected.includes(u.id))
                  }
                  onChange={handleGroupSelectAll}
                />
              </TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedUsers.length > 0 ? (
              displayedUsers.map((user: any) => {
                const isAlreadyAdded = selectedUserIds.includes(user.id);
                const isNewlySelected = newlySelected.includes(user.id);
                
                return (
                  <TableRow
                    key={user.id}
                    className={
                      isAlreadyAdded
                        ? "bg-gray-100 cursor-not-allowed opacity-60"
                        : isNewlySelected
                        ? "bg-blue-50 cursor-pointer"
                        : "cursor-pointer"
                    }
                    onClick={() => handleRowClick(user.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isAlreadyAdded || isNewlySelected}
                        disabled={isAlreadyAdded}
                        onChange={() => toggleUser(user.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {user.name}
                      {isAlreadyAdded && (
                        <span className="ml-2 text-xs text-gray-500">(Already added)</span>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">
                  No students found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end mt-4 gap-2">
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSelect} className="bg-blue-600 text-white">
          Add Selected
        </Button>
      </div>
    </Dialog>
  );
};

export default StudentSelectModal;
